import { ArrowRight, Plug } from 'lucide-react';
import { Link } from 'react-router-dom';

import { rotuloDoPapel } from '@/nucleo/auth/papeis';
import { useAuth } from '@/nucleo/auth/useAuth';
import { AREA_ADMIN } from '@/nucleo/navegacao/mapa';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';

/**
 * `/admin` — tela de entrada da administração.
 *
 * Mesmo escopo de fase 1 da operação: estrutura de navegação, tela de entrada e
 * menu da conta com sair (docs/PLANO-DE-FASES.md).
 *
 * A administração só é alcançada por `ADMIN` e `GESTOR_ADMIN`
 * (docs/AUTENTICACAO-E-ACESSO.md, seção 9) — a lista de papéis está no mapa de
 * navegação, e é ela que a rota protegida usa.
 */
export function AdminInicio() {
  const { usuario } = useAuth();

  if (!usuario) {
    return null;
  }

  const daFase1 = AREA_ADMIN.grupos
    .flatMap((grupo) => grupo.itens)
    .filter((item) => item.fase === 1 && item.caminho !== AREA_ADMIN.raiz);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Administração</h1>
        <p className="text-sm text-muted-foreground">
          Configuração do sistema, acesso das pessoas e integrações.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Disponível agora</CardTitle>
            <CardDescription>
              O que já existe nesta área. Os módulos de acesso, parametrização e relatórios entram
              na fase 8.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {daFase1.length > 0 ? (
              <ul className="space-y-1">
                {daFase1.map((item) => {
                  const Icone = item.icone;
                  return (
                    <li key={item.caminho}>
                      <Link
                        to={item.caminho}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                      >
                        <Icone
                          className="h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span className="flex-1 truncate">{item.titulo}</span>
                        <ArrowRight
                          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Plug className="h-4 w-4" aria-hidden="true" />
                Nenhum módulo de administração disponível nesta fase.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Seu acesso</CardTitle>
            <CardDescription>Perfil com que você entrou nesta área.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="font-medium">{usuario.nome ?? 'Usuário'}</p>
            <div className="flex flex-wrap gap-1.5">
              {usuario.papeis.map((papel) => (
                <Badge
                  key={papel}
                  variant={papel === usuario.papelPrincipal ? 'default' : 'secondary'}
                >
                  {rotuloDoPapel(papel)}
                </Badge>
              ))}
            </div>
            <p className="leading-relaxed text-muted-foreground">
              Papel é atribuição do provedor de identidade, sincronizada a cada entrada. Não se
              troca de perfil pela tela.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminInicio;
